import React, { useMemo } from 'react';
import { i18n } from '@secretescapes/i18n';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useTranslations } from '@utils/useTranslations';
import { RetryBox } from '@features/Sale/RetryBox/RetryBox';
import { getFinalRates } from '../AvailabilityPresentation.utils';
import { useSelectionState } from '../SelectionContext';
import { OfferCard } from './OfferCard';
import { ExtendedOffer } from './Offers.interfaces';
import { Grid } from './Offers.styles';
import {
  getFlightsDescription,
  getInfoBoxType,
  getTravellersFromFacets,
  isBookable,
  showDisclaimer,
  sortOffers,
} from './Offers.utils';

interface Props {
  offers: ExtendedOffer[];
  noAvailabilityReturned: boolean;
  onBookButtonClicked: (offerId: string) => void;
  numberOfRooms: number;
  openCalendar: (offerId: string) => void;
  isLoading: boolean;
  flightsSelected: boolean;
  hasNoFlights: boolean;
}

export const Offers = ({
  offers,
  noAvailabilityReturned,
  onBookButtonClicked,
  numberOfRooms,
  openCalendar,
  isLoading,
  flightsSelected,
  hasNoFlights,
}: Props) => {
  const { territoryLocale, currency, isMobile } = useApplicationState();
  const { finalRates } = useAppState();
  const { travellersSelection } = useSelectionState();
  const { translations } = useTranslations();

  const travellersHOSelectionEnabled = useFeatureFlag(BullfinchFeatureFlags.travellersHOSelection);
  const isRelevantTravellersSelectionEnabled =
    !!flightsSelected || (!flightsSelected && travellersHOSelectionEnabled);

  const determineInfoBox = (offer: ExtendedOffer) => {
    return getInfoBoxType(
      finalRates.show,
      numberOfRooms,
      offer,
      noAvailabilityReturned,
      isRelevantTravellersSelectionEnabled ? travellersSelection : undefined
    );
  };

  const sortedOffers = sortOffers(offers, determineInfoBox);

  const showPhotos = useMemo(() => offers.every(offer => offer?.room?.photos?.length), [offers]);

  if (isLoading) {
    return (
      <Skeleton
        numberOfCards={offers.length}
        showOfferCardButton={finalRates.show}
        showPhoto={showPhotos}
      />
    );
  }

  if (hasNoFlights) {
    return (
      <RetryBox
        retryFunction={() => {
          setURLParams('tripType', 'hotelOnly');
          window?.location?.reload();
        }}
        subtitle={i18n('sale.noflights.text', [], translations)}
        buttonText={i18n('sale.navigate.hotelonly', [], translations)}
        isMobile={isMobile}
      />
    );
  }

  return (
    <Grid>
      {sortedOffers.map(offer => {
        const { description, pricing } = offer;

        const rates = getFinalRates(offer.receipt, numberOfRooms, currency, territoryLocale);

        const infoBoxType = determineInfoBox(offer);

        const showLabelDisclaimer =
          isRelevantTravellersSelectionEnabled &&
          showDisclaimer(travellersSelection, offer.pricing?.packagePriceBasis);

        return (
          <OfferCard
            key={offer.id}
            offer={offer}
            isRefundableCheckInDate={offer.receipt?.isRefundable ?? undefined}
            isBookable={isBookable(finalRates.show, offer.receipt)}
            onBookButtonClicked={onBookButtonClicked}
            finalRate={rates?.finalRate ?? ''}
            finalRatePerRoomPerNight={rates?.finalRatePerRoomPerNight ?? ''}
            availableRoomCount={offer.receipt?.maxAvailability ?? undefined}
            openCalendar={openCalendar}
            infoBoxType={isLoading ? null : infoBoxType}
            description={
              flightsSelected
                ? getFlightsDescription(description ?? '', translations)
                : (description ?? '')
            }
            numberOfTravellers={
              offer.receipt
                ? finalRates.numberOfTravellers
                : getTravellersFromFacets(pricing?.packagePriceBasis)
            }
            numberOfNights={finalRates.numberOfNights}
            showLabelDisclaimer={showLabelDisclaimer}
            flightsSelected={flightsSelected}
            showPhotos={showPhotos}
            numberOfRooms={numberOfRooms}
          />
        );
      })}
    </Grid>
  );
};

interface OffersSkeletonProps {
  numberOfCards: number;
  showOfferCardButton?: boolean;
  showPhoto: boolean;
}

const Skeleton = ({ numberOfCards, showOfferCardButton, showPhoto }: OffersSkeletonProps) => {
  return (
    <Grid>
      {Array.from({ length: numberOfCards }, (_, index) => (
        <OfferCard.Skeleton
          key={`skeleton-${index}`}
          showButton={!!showOfferCardButton}
          showPhoto={showPhoto}
        />
      ))}
    </Grid>
  );
};

Offers.Skeleton = Skeleton;
